import React, { useState } from 'react';
import { Save, Loader2, Target, Database, Check, X, AlertTriangle, MinusCircle, Trash2, Shield, ChevronDown, ExternalLink, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useTechniqueMitigations } from '../../hooks/useTechniqueMitigations';

// Simple Status Selector Component
const StatusSelector = ({ status, onStatusChange }) => {
    const { t } = useTranslation();

    const statuses = [
        {
            id: 'non_corrige',
            label: t('workshop4.ctiDisplay.notFixed'),
            icon: <X className="h-4 w-4" />,
            color: 'bg-red-500 hover:bg-red-600',
            textColor: 'text-white'
        },
        {
            id: 'corrige',
            label: t('workshop4.ctiDisplay.fixed'),
            icon: <Check className="h-4 w-4" />,
            color: 'bg-green-500 hover:bg-green-600',
            textColor: 'text-white'
        },
        {
            id: 'risque_accepte',
            label: t('workshop4.ctiDisplay.riskAccepted'),
            icon: <AlertTriangle className="h-4 w-4" />,
            color: 'bg-yellow-500 hover:bg-yellow-600',
            textColor: 'text-white'
        },
        {
            id: 'non_pertinent',
            label: t('workshop4.ctiDisplay.notRelevant'),
            icon: <MinusCircle className="h-4 w-4" />,
            color: 'bg-gray-500 hover:bg-gray-600',
            textColor: 'text-white'
        }
    ];

    return (
        <div className="flex flex-wrap gap-2 mt-4">
            {statuses.map(s => (
                <button
                    key={s.id}
                    onClick={() => onStatusChange(s.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${status === s.id
                        ? `${s.color} ${s.textColor} shadow-lg scale-105`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                >
                    {s.icon}
                    {s.label}
                </button>
            ))}
        </div>
    );
};

const CTIDisplaySimple = ({ results, onSave, onDelete, isSaving, itemStatuses = {}, onStatusChange, attackPathName, onDeleteAsset, onDeleteItem }) => {
    const { t } = useTranslation();
    const [severityFilter, setSeverityFilter] = useState('ALL');
    const [originFilter, setOriginFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState('severity');
    const [activeTab, setActiveTab] = useState('vulnerabilities');
    const [expandedItems, setExpandedItems] = useState({});
    const [deletingAssetId, setDeletingAssetId] = useState(null); // asset pending delete confirmation
    const [deletingItemId, setDeletingItemId] = useState(null); // individual item (vuln/technique) pending delete
    const [deletingItemType, setDeletingItemType] = useState(null); // 'vulnerability' or 'technique'
    const [showTopOnly, setShowTopOnly] = useState(true); // Slice 2: top 5 prioritized
    const [copiedDossier, setCopiedDossier] = useState(null); // dossier copy feedback

    // Unified mitigations for classic + scan techniques (embedded first, batch-fetch missing)
    const allTechniquesFlat = (results?.assets || []).flatMap(
      (asset) => asset.attackTechniques || asset.techniques || []
    );
    const { map: mitigationMap } = useTechniqueMitigations(allTechniquesFlat);
    const mitigationsFor = (technique) => {
      if (Array.isArray(technique?.mitigations) && technique.mitigations.length > 0) {
        return technique.mitigations;
      }
      const id = String(technique?.techniqueId || technique?.id || '').trim().toUpperCase();
      return mitigationMap[id] || [];
    };

    console.log('[CTIDisplaySimple] Results:', results);

    const buildDossierSentence = (vuln, technique, asset) => {
        const epssNum = vuln.epss != null ? Number(vuln.epss) : null;
        const kevLabel = vuln.kev ? 'Exploité en wild (CISA KEV)' : null;
        const euvdLabel = vuln.euvdExploited ? 'Exploité (EUVD)' : null;
        let epssLabel = null;
        if (epssNum != null && !isNaN(epssNum)) {
            const pct = (epssNum * 100).toFixed(0);
            if (epssNum >= 0.7) epssLabel = `Très probable (<30j, EPSS ${pct}%)`;
            else if (epssNum >= 0.3) epssLabel = `Probable (EPSS ${pct}%)`;
            else epssLabel = `Peu probable (EPSS ${pct}%)`;
        }
        const signals = [kevLabel, euvdLabel, epssLabel].filter(Boolean).join(', ') || 'à prioriser';
        const techClause = technique ? ` via ${technique.id} ${technique.name}` : '';
        const path = attackPathName || results.attackPath?.referenceCode || results.attackPath?.pathName || '';
        // Varied nudge per level
        let nudge = 'Soutient: Faible / non déterminant — à confirmer';
        if (vuln.kev || vuln.euvdExploited || (epssNum != null && epssNum >= 0.7)) nudge = 'Soutient vraisemblance: Élevée — à confirmer (suggestion)';
        else if ((epssNum != null && epssNum >= 0.3) || (vuln.severity?.score >= 7 || vuln.cvssScore >= 7)) nudge = 'Soutient vraisemblance: Moyenne — à confirmer';
        return `${vuln.id} (${signals})${techClause} sur ${asset.name}${path ? ` — scénario ${path}` : ''}. ${nudge}.`;
    };

    const handleCopyDossier = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopiedDossier(key); setTimeout(()=>setCopiedDossier(null), 1500); } catch {}
    };

    if (!results || !results.assets || results.assets.length === 0) {
        return (
            <div className="text-center py-12">
                <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">{t('workshop4.ctiDisplay.noResults')}</p>
            </div>
        );
    }

    const getSeverity = (vuln) => {
        if (vuln?.severity && typeof vuln.severity === 'object') {
            if (vuln.severity.severity) return vuln.severity.severity.toUpperCase();
            if (vuln.severity.severityLevel) return vuln.severity.severityLevel.toUpperCase();
        }

        if (vuln?.severity && typeof vuln.severity === 'string') {
            const upperSev = vuln.severity.toUpperCase();
            if (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO', 'UNKNOWN'].includes(upperSev)) {
                return upperSev;
            }
        }

        const score = vuln?.severity?.score || vuln?.score || vuln?.cvssScore || vuln?.baseScore || vuln?.severity?.severityScore;
        if (!score || score === 'N/A' || score === null || score === undefined) return 'UNKNOWN';

        const numScore = parseFloat(score);
        if (isNaN(numScore)) return 'UNKNOWN';

        if (numScore >= 9.0) return 'CRITICAL';
        else if (numScore >= 7.0) return 'HIGH';
        else if (numScore >= 4.0) return 'MEDIUM';
        else if (numScore >= 0.1) return 'LOW';
        else return 'UNKNOWN';
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'CRITICAL': return 'bg-red-500 text-white';
            case 'HIGH': return 'bg-orange-500 text-white';
            case 'MEDIUM': return 'bg-yellow-500 text-white';
            case 'LOW': return 'bg-green-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'corrige': return 'border-l-green-500 bg-green-50';
            case 'risque_accepte': return 'border-l-yellow-500 bg-yellow-50';
            case 'non_pertinent': return 'border-l-gray-500 bg-gray-50';
            default: return 'border-l-red-500 bg-red-50';
        }
    };

    const filterAndSortVulnerabilities = (vulnerabilities) => {
        let filtered = vulnerabilities;

        if (originFilter === 'pentest') {
            filtered = filtered.filter(vuln => vuln._origin === 'pentest');
        } else if (originFilter === 'diagram') {
            filtered = filtered.filter(vuln => vuln._origin === 'diagram');
        } else if (originFilter === 'cti') {
            filtered = filtered.filter(vuln => vuln._origin !== 'pentest' && vuln._origin !== 'diagram');
        }

        if (severityFilter !== 'ALL') {
            filtered = vulnerabilities.filter(vuln => getSeverity(vuln) === severityFilter);
        }

        if (sortOrder === 'severity') {
            const severityOrder = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3, 'UNKNOWN': 4 };
            filtered.sort((a, b) => {
                const severityA = getSeverity(a);
                const severityB = getSeverity(b);
                return severityOrder[severityA] - severityOrder[severityB];
            });
        } else if (sortOrder === 'alphabetical') {
            filtered.sort((a, b) => a.id.localeCompare(b.id));
        }

        return filtered;
    };

    const getSeverityCounts = () => {
        const counts = { ALL: 0, CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, UNKNOWN: 0 };
        results.assets?.forEach(asset => {
            asset.vulnerabilities?.forEach(vuln => {
                const severity = getSeverity(vuln);
                counts[severity]++;
                counts.ALL++;
            });
        });
        return counts;
    };

    const severityCounts = getSeverityCounts();

    const calculateStatusDistribution = () => {
        const distribution = {
            non_corrige: 0,
            corrige: 0,
            risque_accepte: 0,
            non_pertinent: 0
        };

        results.assets?.forEach(asset => {
            asset.vulnerabilities?.forEach(vuln => {
                const status = itemStatuses[vuln.id] || 'non_pertinent';
                if (distribution[status] !== undefined) {
                    distribution[status]++;
                }
            });
        });

        return distribution;
    };

    const statusDistribution = calculateStatusDistribution();
    const totalItems = (results.totalVulnerabilities || 0) + (results.totalAttackTechniques || 0);

    const toggleDetails = (itemId) => {
        setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    // Handle individual item deletion
    const handleDeleteItem = (itemId, itemType, assetId) => {
        if (onDeleteItem) {
            onDeleteItem(itemId, itemType, assetId);
        }
        setDeletingItemId(null);
        setDeletingItemType(null);
    };

    // Export a single asset's vulnerabilities and techniques to PDF
    const handleExportAsset = (asset) => {
        // Decode CVSS Vector to human-readable format
        const decodeCVSSVector = (vector) => {
            if (!vector) return '';

            const mappings = {
                'AV': { 'N': 'Network', 'A': 'Adjacent', 'L': 'Local', 'P': 'Physical' },
                'AC': { 'L': 'Low', 'H': 'High' },
                'Au': { 'N': 'None', 'S': 'Single', 'M': 'Multiple' },
                'C': { 'N': 'None', 'P': 'Partial', 'C': 'Complete' },
                'I': { 'N': 'None', 'P': 'Partial', 'C': 'Complete' },
                'A': { 'N': 'None', 'P': 'Partial', 'C': 'Complete' }
            };

            const parts = vector.split('/');
            return parts.map(part => {
                const [code, value] = part.split(':');
                const label = mappings[code]?.[value] || value;
                return `${code}:${label}`;
            }).join(' | ');
        };

        // Map status to translation key
        const statusKeyMap = {
            'non_corrige': 'notFixed',
            'corrige': 'fixed',
            'risque_accepte': 'riskAccepted',
            'non_pertinent': 'notRelevant'
        };

        // Determine if we should filter by current severity filter
        const vulnsToExport = severityFilter === 'ALL'
            ? (asset.vulnerabilities || [])
            : (asset.vulnerabilities || []).filter(vuln => getSeverity(vuln) === severityFilter);

        const techniques = asset.techniques || asset.attackTechniques || [];

        // If no items to export, show alert
        if (vulnsToExport.length === 0 && techniques.length === 0) {
            alert(t('workshop4.ctiDisplay.noResultsToExport', 'Aucun résultat à exporter'));
            return;
        }

        // Create PDF
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        // Set PDF metadata
        pdf.setProperties({
            title: `Rapport CTI - ${asset.name}`,
            subject: 'EBIOS RM - Atelier 4 - Intelligence des Menaces',
            author: 'Système EBIOS RM',
            creator: 'EBIOS RM CTI Report Generator'
        });

        // EBIOS RM color scheme
        const colors = {
            headerBlue: [41, 82, 163],       // #2953A3
            primaryBlue: [37, 99, 235],      // #2563EB
            secondaryBlue: [30, 64, 175],    // #1E40AF
            text: [0, 0, 0],
            textGray: [107, 114, 128],
            white: [255, 255, 255],
            lightGray: [235, 235, 235],
            lighterGray: [245, 245, 245],
            critical: [220, 38, 38],
            high: [249, 115, 22],
            medium: [234, 179, 8],
            low: [34, 197, 94]
        };

        let currentPage = 1;

        // ==========================================
        // PAGE 1: COVER PAGE (Matching Context PDF)
        // ==========================================
        
        // Clean white background
        pdf.setFillColor(...colors.white);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        // Header table - Programme info
        pdf.setFillColor(...colors.white);
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        pdf.rect(15, 15, 180, 10, 'S');

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Programme :', 17, 21);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Analyse des risques – EBIOS RM', 45, 21);

        // Internal classification label
        pdf.setFillColor(200, 200, 200);
        pdf.rect(135, 15, 60, 10, 'FD');
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Interne', 155, 21);
        pdf.setTextColor(0, 0, 0);

        // Document type
        pdf.setFillColor(...colors.white);
        pdf.rect(15, 25, 180, 8, 'S');
        pdf.setFont('helvetica', 'bold');
        pdf.text('Document :', 17, 30);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Rapport Intelligence des Menaces', 45, 30);

        // Version and Reference
        pdf.rect(15, 33, 90, 8, 'S');
        pdf.setFont('helvetica', 'bold');
        pdf.text('Version :', 17, 38);
        pdf.setFont('helvetica', 'normal');
        pdf.text('1.0', 37, 38);

        pdf.rect(105, 33, 90, 8, 'S');
        pdf.setFont('helvetica', 'bold');
        pdf.text('Réf :', 107, 38);
        pdf.setFont('helvetica', 'normal');
        pdf.text('EBIOSRM_ATELIER4_CTI', 120, 38);

        // Main title
        pdf.setFontSize(18);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...colors.primaryBlue);
        pdf.text('Rapport d\'Intelligence des Menaces:', 105, 65, { align: 'center' });

        pdf.setFontSize(18);
        pdf.text(asset.name, 105, 80, { align: 'center' });

        // EBIOS RM title
        pdf.setFontSize(24);
        pdf.setTextColor(...colors.primaryBlue);
        pdf.text('EBIOS RM - Atelier 4', 105, 110, { align: 'center' });
        pdf.setTextColor(0, 0, 0);

        // Information table
        const tableStartY = 135;
        const rowHeight = 8;

        const tableData = [
            ['Actif analysé', asset.name],
            ['Vulnérabilités identifiées', String(vulnsToExport.length)],
            ['Techniques MITRE ATT&CK', String(techniques.length)],
            ['Classification', 'Interne'],
            ['Statut', 'Complété'],
            ['Date de génération', new Date().toLocaleDateString('fr-FR')]
        ];

        tableData.forEach((row, index) => {
            const y = tableStartY + (index * rowHeight);

            // Label column (blue background)
            pdf.setFillColor(...colors.headerBlue);
            pdf.rect(15, y, 65, rowHeight, 'FD');
            pdf.setTextColor(...colors.white);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'bold');
            pdf.text(row[0], 17, y + 5.5);

            // Value column (light gray background)
            pdf.setFillColor(...colors.lightGray);
            pdf.rect(80, y, 115, rowHeight, 'FD');
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'normal');

            // Handle long text
            const maxWidth = 110;
            const lines = pdf.splitTextToSize(row[1], maxWidth);
            if (lines.length > 1) {
                const truncated = row[1].length > 50 ? row[1].substring(0, 47) + '...' : row[1];
                pdf.text(truncated, 82, y + 5.5);
            } else {
                pdf.text(row[1], 82, y + 5.5);
            }
        });

        // Version history table
        const historyY = 200;
        const headerHeight = 10;
        const dataRowHeight = 15;

        const headers = ['Version', 'Date', 'Description, remarques', 'Auteur', 'Vérificateur', 'Approbateur'];
        const colWidths = [18, 22, 70, 30, 20, 20];
        let xPos = 15;

        // Draw header row
        headers.forEach((header, i) => {
            pdf.setFillColor(...colors.headerBlue);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.rect(xPos, historyY, colWidths[i], headerHeight, 'FD');

            pdf.setTextColor(...colors.white);
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'bold');

            const maxHeaderWidth = colWidths[i] - 3;
            const headerLines = pdf.splitTextToSize(header, maxHeaderWidth);
            const startY = historyY + (headerHeight / 2) - ((headerLines.length - 1) * 1.5);

            headerLines.forEach((line, lineIndex) => {
                pdf.text(line, xPos + 1.5, startY + (lineIndex * 3));
            });

            xPos += colWidths[i];
        });

        // Add data row
        xPos = 15;
        const versionData = [
            '1.0',
            new Date().toLocaleDateString('fr-FR'),
            'Rapport CTI initial',
            '',
            '',
            ''
        ];

        versionData.forEach((data, i) => {
            pdf.setFillColor(...colors.lighterGray);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.rect(xPos, historyY + headerHeight, colWidths[i], dataRowHeight, 'FD');

            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(7);
            pdf.setFont('helvetica', 'normal');

            if (data) {
                const maxColWidth = colWidths[i] - 3;
                const lines = pdf.splitTextToSize(data, maxColWidth);
                const maxLines = Math.floor((dataRowHeight - 2) / 3);
                const displayLines = lines.slice(0, maxLines);

                const startY = historyY + headerHeight + (dataRowHeight / 2) - ((displayLines.length - 1) * 1.5);
                displayLines.forEach((line, lineIndex) => {
                    pdf.text(line, xPos + 1.5, startY + (lineIndex * 3));
                });
            }
            xPos += colWidths[i];
        });

        // Footer
        const footerY = pageHeight - 20;
        pdf.setFontSize(8);
        pdf.setTextColor(...colors.textGray);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Méthodologie EBIOS RM - Atelier 4: Intelligence des Menaces', 105, footerY, { align: 'center' });
        pdf.text('Document confidentiel - Usage interne uniquement', 105, footerY + 8, { align: 'center' });

        // ==========================================
        // CONTENT PAGES HELPER FUNCTIONS
        // ==========================================
        
        const addPageHeader = () => {
            const headerY = 15;
            
            // Header line
            pdf.setDrawColor(229, 231, 235);
            pdf.setLineWidth(0.5);
            pdf.line(20, headerY + 5, pageWidth - 20, headerY + 5);

            // Document title on left
            pdf.setFontSize(8);
            pdf.setTextColor(...colors.textGray);
            pdf.setFont('helvetica', 'normal');
            pdf.text('Rapport Intelligence des Menaces', 20, headerY);
            pdf.text('Atelier 4', pageWidth - 20, headerY, { align: 'right' });
        };

        const addPageFooter = (pageNum) => {
            const footerY = pageHeight - 10;
            
            // Footer line
            pdf.setDrawColor(229, 231, 235);
            pdf.setLineWidth(0.5);
            pdf.line(20, footerY - 5, pageWidth - 20, footerY - 5);

            pdf.setFontSize(8);
            pdf.setTextColor(...colors.textGray);
            pdf.setFont('helvetica', 'normal');
            pdf.text('Rapport EBIOS RM', 20, footerY);
            pdf.text('Confidentiel - Usage interne', pageWidth / 2, footerY, { align: 'center' });
            pdf.text(`Page ${pageNum}`, pageWidth - 20, footerY, { align: 'right' });
        };

        const startNewPage = () => {
            pdf.addPage();
            currentPage++;
            addPageHeader();
            addPageFooter(currentPage);
        };

        // ==========================================
        // VULNERABILITIES SECTION
        // ==========================================
        if (vulnsToExport.length > 0) {
            startNewPage();
            
            // Section title
            pdf.setFontSize(18);
            pdf.setTextColor(...colors.secondaryBlue);
            pdf.setFont('helvetica', 'bold');
            pdf.text('1. VULNÉRABILITÉS IDENTIFIÉES', 20, 30);

            // Introduction
            let currentY = 45;
            pdf.setFontSize(10);
            pdf.setTextColor(51, 65, 85);
            pdf.setFont('helvetica', 'normal');
            
            const introText = `Cette section présente les vulnérabilités identifiées pour l'actif "${asset.name}". ` +
                `Ces vulnérabilités ont été découvertes via l'analyse CTI et doivent être traitées selon leur niveau de criticité.`;
            
            const introLines = pdf.splitTextToSize(introText, 170);
            introLines.forEach(line => {
                pdf.text(line, 20, currentY);
                currentY += 6;
            });
            currentY += 10;

            // Prepare vulnerability data
            const vulnTableData = vulnsToExport.map(vuln => {
                let severityStr;
                if (vuln.severity && typeof vuln.severity === 'object') {
                    severityStr = vuln.severity.severity || vuln.severity.severityLevel || 'UNKNOWN';
                } else if (typeof vuln.severity === 'string') {
                    severityStr = vuln.severity;
                } else {
                    const score = vuln.cvssScore || vuln.baseScore || vuln.score;
                    if (score >= 9.0) severityStr = 'CRITICAL';
                    else if (score >= 7.0) severityStr = 'HIGH';
                    else if (score >= 4.0) severityStr = 'MEDIUM';
                    else if (score >= 0.1) severityStr = 'LOW';
                    else severityStr = 'UNKNOWN';
                }

                const status = itemStatuses[vuln.id] || 'non_pertinent';
                const statusTranslationKey = statusKeyMap[status] || 'notRelevant';
                const score = vuln.cvssScore || vuln.baseScore || vuln.score || vuln.severity?.score || 'N/A';
                const vector = vuln.severity?.vector || vuln.vector || '';
                const vectorReadable = decodeCVSSVector(vector);
                
                return [
                    vuln.id,
                    severityStr,
                    score,
                    vectorReadable,
                    (vuln.description || '').substring(0, 80) + ((vuln.description || '').length > 80 ? '...' : ''),
                    t(`workshop4.ctiDisplay.${statusTranslationKey}`)
                ];
            });

            // Add vulnerabilities table
            pdf.autoTable({
                startY: currentY,
                head: [['ID', 'Sévérité', 'Score', 'Vecteur CVSS', 'Description', 'Statut']],
                body: vulnTableData,
                theme: 'grid',
                margin: { top: 30, bottom: 20, left: 20, right: 20 },
                headStyles: {
                    fillColor: colors.headerBlue,
                    textColor: colors.white,
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    lineColor: [226, 232, 240],
                    lineWidth: 0.5
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252]
                },
                columnStyles: {
                    0: { cellWidth: 30, fontStyle: 'bold' },
                    1: { cellWidth: 20, halign: 'center' },
                    2: { cellWidth: 15, halign: 'center' },
                    3: { cellWidth: 50 },
                    4: { cellWidth: 'auto' },
                    5: { cellWidth: 25, halign: 'center' }
                },
                didParseCell: function(data) {
                    // Color code severity
                    if (data.section === 'body' && data.column.index === 1) {
                        const severity = data.cell.raw;
                        if (severity === 'CRITICAL') {
                            data.cell.styles.fillColor = [254, 226, 226];
                            data.cell.styles.textColor = colors.critical;
                            data.cell.styles.fontStyle = 'bold';
                        } else if (severity === 'HIGH') {
                            data.cell.styles.fillColor = [255, 237, 213];
                            data.cell.styles.textColor = colors.high;
                        } else if (severity === 'MEDIUM') {
                            data.cell.styles.fillColor = [254, 249, 195];
                            data.cell.styles.textColor = [161, 98, 7];
                        } else if (severity === 'LOW') {
                            data.cell.styles.fillColor = [220, 252, 231];
                            data.cell.styles.textColor = colors.low;
                        }
                    }
                }
            });
        }

        // ==========================================
        // TECHNIQUES SECTION
        // ==========================================
        if (techniques.length > 0) {
            // Check if we need a new page
            if (pdf.lastAutoTable && pdf.lastAutoTable.finalY > pageHeight - 80) {
                startNewPage();
            } else if (vulnsToExport.length === 0) {
                startNewPage();
            } else {
                pdf.addPage();
                currentPage++;
                addPageHeader();
                addPageFooter(currentPage);
            }
            
            // Section title
            pdf.setFontSize(18);
            pdf.setTextColor(...colors.secondaryBlue);
            pdf.setFont('helvetica', 'bold');
            pdf.text('2. TECHNIQUES MITRE ATT&CK', 20, 30);

            // Introduction
            let currentY = 45;
            pdf.setFontSize(10);
            pdf.setTextColor(51, 65, 85);
            pdf.setFont('helvetica', 'normal');
            
            const introText = `Cette section présente les techniques d'attaque MITRE ATT&CK identifiées pour l'actif "${asset.name}". ` +
                `Ces techniques représentent les méthodes potentielles qu'un attaquant pourrait utiliser.`;
            
            const introLines = pdf.splitTextToSize(introText, 170);
            introLines.forEach(line => {
                pdf.text(line, 20, currentY);
                currentY += 6;
            });
            currentY += 10;
            
            // Prepare technique data
            const techTableData = techniques.map(technique => {
                const techniqueId = technique.id || technique.techniqueId || 'N/A';
                const mitreUrl = technique.url || (techniqueId !== 'N/A' ? `https://attack.mitre.org/techniques/${techniqueId.replace('.', '/')}` : '');
                
                return [
                    techniqueId,
                    technique.name || technique.techniqueName || '',
                    technique.tactic || '',
                    (technique.description || '').substring(0, 60) + ((technique.description || '').length > 60 ? '...' : ''),
                    mitreUrl
                ];
            });

            // Add techniques table
            pdf.autoTable({
                startY: currentY,
                head: [['ID Technique', 'Nom', 'Tactique', 'Description', 'URL MITRE']],
                body: techTableData,
                theme: 'grid',
                margin: { top: 30, bottom: 20, left: 20, right: 20 },
                headStyles: {
                    fillColor: colors.headerBlue,
                    textColor: colors.white,
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'center'
                },
                styles: {
                    fontSize: 8,
                    cellPadding: 3,
                    lineColor: [226, 232, 240],
                    lineWidth: 0.5
                },
                alternateRowStyles: {
                    fillColor: [248, 250, 252]
                },
                columnStyles: {
                    0: { cellWidth: 25, fontStyle: 'bold', halign: 'center' },
                    1: { cellWidth: 40 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 'auto' },
                    4: { cellWidth: 50 }
                }
            });
        }

        // Generate and download file
        const sanitizedAssetName = asset.name.replace(/[^a-zA-Z0-9_-]/g, '_');
        const dateStr = new Date().toISOString().split('T')[0];
        pdf.save(`CTI_${sanitizedAssetName}_${dateStr}.pdf`);
    };

    const renderVulnerabilitiesContent = () => {
        const hasVulnerabilities = results.assets?.some(asset => asset.vulnerabilities?.length > 0);

        if (!hasVulnerabilities) {
            return (
                <div className="text-center py-12">
                    <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">{t('workshop4.ctiDisplay.noVulnerabilities')}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {results.assets.map(asset => {
                    if (!asset.vulnerabilities || asset.vulnerabilities.length === 0) return null;

                    const isPendingDelete = deletingAssetId === asset.id;

                    return (
                        <div key={asset.id} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-slate-800 flex items-center">
                                    <Database className="h-5 w-5 mr-2 text-blue-600" />
                                    {asset.name}
                                    {asset._fromCache && (
                                        <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                            {t('workshop4.ctiDisplay.shared', 'Partagé')}
                                        </span>
                                    )}
                                    {(asset._fromPentest || (asset.vulnerabilities || []).some((v) => v._origin === 'pentest')) && (
                                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-purple-600 text-white rounded-full">
                                            {t('workshop4.pentest.badge', 'Scan')}
                                        </span>
                                    )}
                                    {(asset.vulnerabilities || []).some((v) => v._origin === 'diagram') && (
                                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-teal-600 text-white rounded-full">
                                            {t('workshop4.networkDiagram.badge', 'Schéma')}
                                        </span>
                                    )}
                                </h4>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleExportAsset(asset)}
                                        title={t('workshop4.ctiDisplay.exportAssetResults', 'Exporter les résultats de cet actif')}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                                    >
                                        <Download className="h-4 w-4" />
                                        {t('workshop4.ctiDisplay.exportAsset', 'Exporter')}
                                    </button>
                                    {onDeleteAsset && (
                                        <button
                                            onClick={() => setDeletingAssetId(isPendingDelete ? null : asset.id)}
                                            title={t('workshop4.ctiDisplay.deleteAssetResults', 'Supprimer les résultats de cet actif')}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                isPendingDelete
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                            }`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {isPendingDelete
                                                ? t('common.cancel', 'Annuler')
                                                : t('workshop4.ctiDisplay.deleteResults', 'Supprimer résultats')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Inline confirmation banner */}
                            {isPendingDelete && (
                                <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between gap-4">
                                    <p className="text-sm text-red-700 font-medium">
                                        ⚠️ {t('workshop4.ctiDisplay.confirmDeleteAsset', 'Supprimer les résultats CTI de cet actif sur tous les chemins d\'attaque ?')}
                                    </p>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <button
                                            onClick={() => setDeletingAssetId(null)}
                                            className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700"
                                        >
                                            {t('common.cancel', 'Annuler')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                onDeleteAsset(asset.id, asset.name);
                                                setDeletingAssetId(null);
                                            }}
                                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            {t('common.confirm', 'Confirmer')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="p-4">
                                {(() => {
                                    // Filter first, then apply ranking top-5 slice if enabled
                                    const preFiltered = filterAndSortVulnerabilities(asset.vulnerabilities);
                                    const totalVulns = asset.vulnerabilities.length;
                                    // If ranked, keep ranked order; otherwise keep filter order
                                    const ranked = preFiltered.slice().sort((a, b) => (b._rankScore ?? 0) - (a._rankScore ?? 0));
                                    const hasRanked = ranked.some((v) => v._rankScore !== undefined);
                                    const displayList = hasRanked && showTopOnly ? ranked.slice(0, 5) : ranked;
                                    const filteredCount = preFiltered.length;
                                    const hiddenCount = hasRanked && showTopOnly ? Math.max(0, ranked.length - 5) : 0;

                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-4">
                                                <h5 className="text-md font-semibold text-slate-800">
                                                    {severityFilter === 'ALL' ? (
                                                        <span>{t('workshop4.ctiDisplay.vulnerabilities')} ({totalVulns})</span>
                                                    ) : (
                                                        <span>{t('workshop4.ctiDisplay.vulnerabilities')} {severityFilter} ({filteredCount} sur {totalVulns})</span>
                                                    )}
                                                </h5>
                                                {hasRanked && ranked.length > 5 && (
                                                    <button
                                                        onClick={() => setShowTopOnly(!showTopOnly)}
                                                        className="text-xs px-3 py-1.5 rounded-lg border bg-white hover:bg-slate-50 text-slate-700"
                                                    >
                                                        {showTopOnly ? `Voir tout (${ranked.length})` : 'Voir top 5'}
                                                    </button>
                                                )}
                                            </div>
                                            {hasRanked && (
                                                <div className="mb-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex gap-2">
                                                    <span>ℹ️</span>
                                                    <span>Top 5 triés par <b>risque d'exploitation</b> (Exploité KEV/EUVD &gt; EPSS &gt; récent), pas par gravité CVSS.</span>
                                                </div>
                                            )}
                                            {hasRanked && hiddenCount > 0 && showTopOnly && (
                                                <div className="mb-3 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                                    Top 5 prioritaires. {hiddenCount} masqués — cliquez "Voir tout" pour le reste. Bas-priorité suggéré `non pertinent` (à confirmer).
                                                </div>
                                            )}

                                            {/* Dossier evidence - Slice 3 */}
                                            {hasRanked && (() => {
                                                const top3 = ranked.slice(0, 3);
                                                const techniques = asset.attackTechniques || asset.techniques || [];
                                                if (top3.length === 0) return null;
                                                return (
                                                    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                                                        <div className="text-sm font-semibold text-indigo-800 mb-2">Dossier - preuves pour vraisemblance (suggestion, à confirmer)</div>
                                                        <div className="space-y-2">
                                                            {top3.map((v, i) => {
                                                                const tech = techniques[i % Math.max(1, techniques.length)] || techniques[0] || null;
                                                                const sentence = buildDossierSentence(v, tech, asset);
                                                                const key = `${asset.id}-${v.id}`;
                                                                return (
                                                                    <div key={v.id} className="bg-white rounded border border-indigo-100 p-2 flex gap-2">
                                                                        <span className="text-xs bg-indigo-600 text-white px-1.5 py-0.5 rounded h-fit">#{v._rank}</span>
                                                                        <p className="flex-1 text-xs text-slate-700 leading-relaxed">{sentence}</p>
                                                                        <button onClick={()=>handleCopyDossier(sentence, key)} className="text-xs px-2 py-1 border rounded bg-white hover:bg-slate-50 shrink-0">{copiedDossier===key?'Copié':'Copier'}</button>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                        <p className="text-xs text-slate-500 mt-2">Ne remplit pas automatiquement la matrice de vraisemblance — copiez/collez et ajustez selon votre jugement EBIOS.</p>
                                                    </div>
                                                );
                                            })()}

                                            {displayList.length > 0 ? (
                                                <div className="space-y-3">
                                                    {displayList.map(vuln => {
                                                        const severity = getSeverity(vuln);
                const status = itemStatuses[vuln.id] || 'non_pertinent';
                                                        const score = vuln.severity?.score || vuln.score || vuln.cvssScore || vuln.baseScore || vuln.severity?.severityScore || 'N/A';
                                                        const isExpanded = expandedItems[vuln.id];
                                                        const isPendingDeleteItem = deletingItemId === vuln.id && deletingItemType === 'vulnerability';
                                                        const epssVal = vuln.epss ?? vuln.epssScore ?? null;
                                                        const kevFlag = !!vuln.kev;
                                                        const euvdFlag = !!vuln.euvdExploited || !!vuln.exploited;
                                                        const rankScore = vuln._rankScore;

                                                        const isPentest = vuln._origin === 'pentest';
                                                        const isDiagram = vuln._origin === 'diagram';
                                                        const isPoolItem = isPentest || isDiagram;
                                                        const displayTitle = vuln.cveId || vuln._pentestTitle || vuln.id;

                                                        return (
                                                            <div
                                                                key={vuln.id}
                                                                className={`border-l-4 rounded-lg p-4 ${getStatusColor(status)} ${isPoolItem && !vuln._enriched ? 'border-dashed border-2' : 'border-solid border'}`}
                                                            >
                                                                {isPendingDeleteItem && (
                                                                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 flex items-center justify-between gap-3">
                                                                        <p className="text-sm text-red-700 font-medium">
                                                                            ⚠️ {t('workshop4.ctiDisplay.confirmDeleteItem', 'Supprimer cette vulnérabilité ?')}
                                                                        </p>
                                                                        <div className="flex gap-2 flex-shrink-0">
                                                                            <button
                                                                                onClick={() => { setDeletingItemId(null); setDeletingItemType(null); }}
                                                                                className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 text-slate-700"
                                                                            >
                                                                                {t('common.cancel', 'Annuler')}
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleDeleteItem(vuln.id, 'vulnerability', asset.id)}
                                                                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                                                            >
                                                                                {t('common.confirm', 'Confirmer')}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="flex items-start justify-between">
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                                            <h6 className="font-bold text-lg text-slate-800 flex items-center gap-2">{rankScore !== undefined && <span className="text-xs bg-slate-800 text-white px-1.5 py-0.5 rounded">#{vuln._rank}</span>}{displayTitle}</h6>
                                                                            {isPentest && (
                                                                                <span
                                                                                    title={vuln._pentestFile || t('workshop4.pentest.badge', 'Scan')}
                                                                                    className="px-2 py-0.5 text-xs font-bold bg-purple-600 text-white rounded"
                                                                                >
                                                                                    {t('workshop4.pentest.badge', 'Scan')}
                                                                                </span>
                                                                            )}
                                                                            {isDiagram && (
                                                                                <span
                                                                                    title={vuln._pentestFile || t('workshop4.networkDiagram.badge', 'Schéma')}
                                                                                    className="px-2 py-0.5 text-xs font-bold bg-teal-600 text-white rounded"
                                                                                >
                                                                                    {t('workshop4.networkDiagram.badge', 'Schéma')}
                                                                                </span>
                                                                            )}
                                                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(severity)}`}>
                                                                                {t(`workshop4.ctiDisplay.${severity.toLowerCase()}`)}
                                                                            </span>
                                                                            <span className="text-lg font-bold text-slate-700">
                                                                                {score}
                                                                            </span>
                                                                            {kevFlag && <span className="px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded">KEV</span>}
                                                                            {euvdFlag && <span className="px-2 py-0.5 text-xs font-bold bg-indigo-600 text-white rounded">EUVD exploité</span>}
                                                                            {epssVal !== null && epssVal !== undefined && <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded">EPSS {(Number(epssVal)*100).toFixed(1)}%</span>}
                                                                            {rankScore !== undefined && <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-700 border rounded">Score {rankScore}</span>}
                                                                            <button
                                                                                onClick={() => toggleDetails(vuln.id)}
                                                                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                                            >
                                                                                {t('workshop4.ctiDisplay.details', 'Détails')}
                                                                                <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                                            </button>
                                                                        </div>

                                                                        {rankScore !== undefined && (() => {
                                                                            const epssPct = epssVal != null ? (Number(epssVal)*100).toFixed(0) : null;
                                                                            const isRecent = vuln.publishedDate ? ((Date.now() - new Date(vuln.publishedDate).getTime())/(1000*60*60*24) <= 365) : false;
                                                                            const isOld = vuln.publishedDate ? ((Date.now() - new Date(vuln.publishedDate).getTime())/(1000*60*60*24) > 730) : false;
                                                                            const parts = [];
                                                                            if (kevFlag) parts.push('Exploité en wild (KEV)');
                                                                            else if (euvdFlag) parts.push('Exploité (EUVD)');
                                                                            if (epssVal != null) {
                                                                                if (Number(epssVal) >= 0.7) parts.push(`Très probable ${epssPct}%`);
                                                                                else if (Number(epssVal) >= 0.3) parts.push(`Probable ${epssPct}%`);
                                                                                else parts.push(`Peu probable ${epssPct}%`);
                                                                            }
                                                                            if (isRecent) parts.push('Récent');
                                                                            else if (isOld) parts.push('Ancien');
                                                                            const why = parts.length ? parts.join(' + ') + ` → Score ${rankScore}` : `Score ${rankScore}`;
                                                                            return <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1 mb-2">Pourquoi #{vuln._rank}: {why}</div>;
                                                                        })()}

                                                                        <p className="text-sm text-slate-700 mb-3">
                                                                            {vuln.description}
                                                                        </p>
                                                                        {isPoolItem && vuln._pentestFile && (
                                                                            <p className="text-xs text-slate-500 mb-2">
                                                                                {t('workshop4.pentest.sourceLabel', 'Source : {{name}}', { name: vuln._pentestFile })}
                                                                            </p>
                                                                        )}
                                                                        {isPoolItem && vuln._pentestEvidence && (
                                                                            <p className="text-xs text-purple-700 italic bg-purple-50 border-l-2 border-purple-300 pl-2 py-1 mb-3">
                                                                                « {vuln._pentestEvidence} »{vuln._pentestFile ? ` — ${vuln._pentestFile}` : ''}
                                                                            </p>
                                                                        )}

                                                                        {isExpanded && (
                                                                            <div className="mt-3 p-3 bg-white rounded border border-slate-200 text-sm space-y-2">
                                                                                {rankScore !== undefined && <div><span className="font-medium text-slate-600">Rank score:</span> {rankScore} {kevFlag && '• KEV'} {euvdFlag && '• EUVD exploité'} {epssVal !== null && `• EPSS ${epssVal}`}</div>}
                                                                                {vuln.severity?.vector && (
                                                                                    <div><span className="font-medium text-slate-600">CVSS Vector:</span> <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{vuln.severity.vector}</code></div>
                                                                                )}
                                                                                {(epssVal !== null && epssVal !== undefined) && (
                                                                                    <div><span className="font-medium text-slate-600">EPSS Score:</span> {epssVal} {vuln._enriched && <span className="text-xs text-slate-500">(enrichi)</span>}</div>
                                                                                )}
                                                                                {kevFlag && <div><span className="font-medium text-red-700">CISA KEV: Actively exploited</span></div>}
                                                                                {euvdFlag && <div><span className="font-medium text-indigo-700">EUVD: exploité</span></div>}
                                                                                {vuln.publishedDate && (
                                                                                    <div><span className="font-medium text-slate-600">{t('workshop4.ctiDisplay.published', 'Publié:')}</span> {new Date(vuln.publishedDate).toLocaleDateString()}</div>
                                                                                )}
                                                                                {vuln.lastModified && (
                                                                                    <div><span className="font-medium text-slate-600">{t('workshop4.ctiDisplay.modified', 'Modifié:')}</span> {new Date(vuln.lastModified).toLocaleDateString()}</div>
                                                                                )}
                                                                                {vuln.references && vuln.references.length > 0 && (
                                                                                    <div>
                                                                                        <span className="font-medium text-slate-600">{t('workshop4.ctiDisplay.reference', 'Références')}:</span>
                                                                                        <div className="mt-1 space-y-1">
                                                                                            {(Array.isArray(vuln.references) ? vuln.references : [vuln.references]).slice(0, 3).map((ref, idx) => {
                                                                                                const url = typeof ref === 'string' ? ref : ref.url;
                                                                                                if (!url) return null;
                                                                                                return (
                                                                                                    <a
                                                                                                        key={idx}
                                                                                                        href={url}
                                                                                                        target="_blank"
                                                                                                        rel="noopener noreferrer"
                                                                                                        className="flex items-center gap-1 text-blue-600 hover:underline text-xs"
                                                                                                    >
                                                                                                        <ExternalLink className="h-3 w-3" />
                                                                                                        {url.length > 60 ? url.substring(0, 60) + '...' : url}
                                                                                                    </a>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 ml-4">
                                                                        <button
                                                                            onClick={() => {
                                                                                setDeletingItemId(isPendingDeleteItem ? null : vuln.id);
                                                                                setDeletingItemType(isPendingDeleteItem ? null : 'vulnerability');
                                                                            }}
                                                                            title={t('workshop4.ctiDisplay.deleteItem', 'Supprimer cette vulnérabilité')}
                                                                            className={`p-1.5 rounded-lg transition-colors ${
                                                                                isPendingDeleteItem
                                                                                    ? 'bg-red-600 text-white'
                                                                                    : 'text-red-600 hover:bg-red-50'
                                                                            }`}
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                <StatusSelector
                                                                    status={status}
                                                                    onStatusChange={(newStatus) => onStatusChange && onStatusChange(vuln.id, newStatus)}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center py-8 text-gray-500">
                                                    <Target className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                                                    <p>{t('workshop4.ctiDisplay.noSeverityVulnerabilities', { severity: severityFilter })}</p>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTechniquesContent = () => {
        const hasTechniques = results.assets?.some(asset =>
            (asset.techniques?.length > 0) || (asset.attackTechniques?.length > 0)
        );

        console.log('[CTIDisplaySimple] hasTechniques:', hasTechniques);

        if (!hasTechniques) {
            return (
                <div className="text-center py-12">
                    <Shield className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">{t('workshop4.ctiDisplay.noMitreFound', 'Aucune technique MITRE ATT&CK trouvée')}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {results.assets.map(asset => {
                    const techniques = asset.techniques || asset.attackTechniques || [];
                    if (techniques.length === 0) return null;

                    return (
                        <div key={asset.id} className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-slate-800 flex items-center">
                                    <Database className="h-5 w-5 mr-2 text-blue-600" />
                                    {asset.name}
                                    {asset._fromCache && (
                                        <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                            {t('workshop4.ctiDisplay.shared', 'Partagé')}
                                        </span>
                                    )}
                                    {(asset._fromPentest || techniques.some((tech) => tech._origin === 'pentest')) && (
                                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-purple-600 text-white rounded-full">
                                            {t('workshop4.pentest.badge', 'Scan')}
                                        </span>
                                    )}
                                    {techniques.some((tech) => tech._origin === 'diagram') && (
                                        <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-teal-600 text-white rounded-full">
                                            {t('workshop4.networkDiagram.badge', 'Schéma')}
                                        </span>
                                    )}
                                </h4>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleExportAsset(asset)}
                                        title={t('workshop4.ctiDisplay.exportAssetResults', 'Exporter les résultats de cet actif')}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                                    >
                                        <Download className="h-4 w-4" />
                                        {t('workshop4.ctiDisplay.exportAsset', 'Exporter')}
                                    </button>
                                    {onDeleteAsset && (
                                        <button
                                            onClick={() => setDeletingAssetId(deletingAssetId === asset.id ? null : asset.id)}
                                            title={t('workshop4.ctiDisplay.deleteAssetResults', 'Supprimer les résultats de cet actif')}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                deletingAssetId === asset.id
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                            }`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {deletingAssetId === asset.id
                                                ? t('common.cancel', 'Annuler')
                                                : t('workshop4.ctiDisplay.deleteResults', 'Supprimer résultats')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Inline confirmation banner */}
                            {deletingAssetId === asset.id && (
                                <div className="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center justify-between gap-4">
                                    <p className="text-sm text-red-700 font-medium">
                                        ⚠️ {t('workshop4.ctiDisplay.confirmDeleteAsset', "Supprimer les résultats CTI de cet actif sur tous les chemins d'attaque ?")}
                                    </p>
                                    <div className="flex gap-2 flex-shrink-0">
                                        <button
                                            onClick={() => setDeletingAssetId(null)}
                                            className="px-3 py-1.5 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700"
                                        >
                                            {t('common.cancel', 'Annuler')}
                                        </button>
                                        <button
                                            onClick={() => {
                                                onDeleteAsset(asset.id, asset.name);
                                                setDeletingAssetId(null);
                                            }}
                                            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                                        >
                                            {t('common.confirm', 'Confirmer')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Techniques list */}
                            <div className="p-4 space-y-3">
                                {techniques.map((technique, idx) => {
                                    const techniqueId = technique.id || technique.techniqueId || 'N/A';
                                    const itemKey = `tech_${techniqueId}_${idx}`;
                                    const isExpanded = expandedItems[itemKey];
                                    const mitreUrl = technique.url || (techniqueId !== 'N/A' ? `https://attack.mitre.org/techniques/${techniqueId.replace('.', '/')}` : null);
                                    const isPendingDeleteItem = deletingItemId === itemKey && deletingItemType === 'technique';
                                    const isPentest = technique._origin === 'pentest';
                                    const isDiagram = technique._origin === 'diagram';
                                    const isPoolItem = isPentest || isDiagram;

                                    return (
                                        <div key={idx} className={`border rounded-lg p-4 ${isPoolItem && !technique._enriched ? 'border-purple-400 border-dashed bg-purple-50' : 'border-purple-200 bg-purple-50'}`}>
                                            {isPendingDeleteItem && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 flex items-center justify-between gap-3">
                                                    <p className="text-sm text-red-700 font-medium">
                                                        ⚠️ {t('workshop4.ctiDisplay.confirmDeleteItem', 'Supprimer cette technique ?')}
                                                    </p>
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        <button
                                                            onClick={() => { setDeletingItemId(null); setDeletingItemType(null); }}
                                                            className="px-2 py-1 text-xs bg-white border border-slate-300 rounded hover:bg-slate-50 text-slate-700"
                                                        >
                                                            {t('common.cancel', 'Annuler')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteItem(itemKey, 'technique', asset.id)}
                                                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                                        >
                                                            {t('common.confirm', 'Confirmer')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center space-x-2 flex-wrap gap-2">
                                                    <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-mono font-medium">
                                                        {techniqueId}
                                                    </span>
                                                    <span className="font-medium text-gray-800">
                                                        {technique.name || technique.techniqueName || 'Unknown Technique'}
                                                    </span>
                                                    {isPentest && (
                                                        <span
                                                            title={technique._pentestFile || t('workshop4.pentest.badge', 'Scan')}
                                                            className="px-2 py-0.5 text-xs font-bold bg-purple-600 text-white rounded"
                                                        >
                                                            {t('workshop4.pentest.badge', 'Scan')}
                                                        </span>
                                                    )}
                                                    {isDiagram && (
                                                        <span
                                                            title={technique._pentestFile || t('workshop4.networkDiagram.badge', 'Schéma')}
                                                            className="px-2 py-0.5 text-xs font-bold bg-teal-600 text-white rounded"
                                                        >
                                                            {t('workshop4.networkDiagram.badge', 'Schéma')}
                                                        </span>
                                                    )}
                                                    {isPoolItem && technique._mitreVerified && (
                                                        <span className="px-2 py-0.5 text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded">
                                                            {t('workshop4.pentest.verifiedChip', '✓ MITRE')}
                                                        </span>
                                                    )}
                                                    {technique.tactic && (
                                                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                                                            {technique.tactic}
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={() => toggleDetails(itemKey)}
                                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 hover:bg-purple-100 rounded transition-colors"
                                                    >
                                                        {t('workshop4.ctiDisplay.details', 'Détails')}
                                                        <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setDeletingItemId(isPendingDeleteItem ? null : itemKey);
                                                        setDeletingItemType(isPendingDeleteItem ? null : 'technique');
                                                    }}
                                                    title={t('workshop4.ctiDisplay.deleteItem', 'Supprimer cette technique')}
                                                    className={`p-1.5 rounded-lg transition-colors ${
                                                        isPendingDeleteItem
                                                            ? 'bg-red-600 text-white'
                                                            : 'text-red-600 hover:bg-red-50'
                                                    }`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>

                                            {isExpanded && (
                                                <div className="mt-3 p-3 bg-white rounded border border-purple-200 text-sm space-y-2">
                                                    {isPoolItem && technique._pentestFile && (
                                                        <p className="text-xs text-slate-500">
                                                            {t('workshop4.pentest.sourceLabel', 'Source : {{name}}', { name: technique._pentestFile })}
                                                        </p>
                                                    )}
                                                    {technique.description && (
                                                        <p className="text-gray-700">{technique.description}</p>
                                                    )}
                                                    {isPoolItem && technique.rationale && (
                                                        <div>
                                                            <span className="font-medium text-purple-700">{t('workshop4.pentest.suggestionTitle', 'Suggestion Assistant ACG')}:</span>
                                                            <p className="text-xs text-purple-700 italic mt-0.5 border-l-2 border-purple-300 pl-2">« {technique.rationale} »</p>
                                                        </div>
                                                    )}
                                                    {mitigationsFor(technique).length > 0 && (
                                                        <div>
                                                            <span className="font-medium text-slate-600">{t('workshop4.pentest.mitigationsTitle', 'Mitigations')} ({mitigationsFor(technique).length}):</span>
                                                            <ul className="mt-1 space-y-1">
                                                                {mitigationsFor(technique).map((m, mi) => (
                                                                    <li key={mi} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                                                                        {m.url ? (
                                                                            <a href={m.url} target="_blank" rel="noopener noreferrer" className="font-mono font-bold text-purple-700 hover:underline">
                                                                                {m.id}
                                                                            </a>
                                                                        ) : (
                                                                            <span className="font-mono font-bold">{m.id}</span>
                                                                        )}
                                                                        {' — '}{m.name}
                                                                        {m.description && (
                                                                            <span className="block text-slate-600 mt-0.5">{m.description.length > 300 ? `${m.description.slice(0, 300)}…` : m.description}</span>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {mitreUrl && (
                                                        <a
                                                            href={mitreUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1 text-purple-600 hover:underline text-xs"
                                                        >
                                                            <ExternalLink className="h-3 w-3" />
                                                            {t('workshop4.ctiDisplay.viewOnMitre', 'Voir sur MITRE ATT&CK')}
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                    <div className="text-3xl font-bold text-red-600">{results.totalVulnerabilities || 0}</div>
                    <div className="text-sm text-slate-600">{t('workshop4.ctiDisplay.vulnerabilities')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                    <div className="text-3xl font-bold text-purple-600">{results.totalAttackTechniques || 0}</div>
                    <div className="text-sm text-slate-600">{t('workshop4.ctiDisplay.techniques')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                    <div className="text-3xl font-bold text-blue-600">{results.assets?.length || 0}</div>
                    <div className="text-sm text-slate-600">{t('workshop4.ctiDisplay.assets')}</div>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                    <div className="text-3xl font-bold text-slate-600">{totalItems}</div>
                    <div className="text-sm text-slate-600">{t('workshop4.ctiDisplay.total')}</div>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="flex border-b border-slate-200">
                    <button
                        onClick={() => setActiveTab('vulnerabilities')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'vulnerabilities'
                            ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <AlertTriangle className="inline-block h-4 w-4 mr-2" />
                        {t('workshop4.ctiDisplay.vulnerabilitiesTab', 'Vulnérabilités')} ({results.totalVulnerabilities || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('techniques')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'techniques'
                            ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                    >
                        <Shield className="inline-block h-4 w-4 mr-2" />
                        {t('workshop4.ctiDisplay.techniquesTab', 'Techniques MITRE ATT&CK')} ({results.totalAttackTechniques || 0})
                    </button>
                </div>
            </div>

            {totalItems > 0 && activeTab === 'vulnerabilities' && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">{t('workshop4.ctiDisplay.statusDistribution')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded-full bg-red-500"></div>
                            <span className="text-sm text-slate-600">{t('workshop4.ctiDisplay.notFixed')}: {statusDistribution.non_corrige}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded-full bg-green-500"></div>
                            <span className="text-sm text-slate-600">{t('workshop4.ctiDisplay.fixed')}: {statusDistribution.corrige}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                            <span className="text-sm text-slate-600">{t('workshop4.ctiDisplay.riskAccepted')}: {statusDistribution.risque_accepte}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 rounded-full bg-gray-500"></div>
                            <span className="text-sm text-slate-600">{t('workshop4.ctiDisplay.notRelevant')}: {statusDistribution.non_pertinent}</span>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'vulnerabilities' && (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-semibold text-slate-700 mr-2">{t('workshop4.pentest.originFilter', 'Origine :')}</span>
                        {[
                            { id: 'all', label: t('workshop4.pentest.originAll', 'Tous') },
                            { id: 'pentest', label: t('workshop4.pentest.badge', 'Scan') },
                            { id: 'diagram', label: t('workshop4.networkDiagram.badge', 'Schéma') },
                            { id: 'cti', label: t('workshop4.pentest.originCti', 'CTI classique') },
                        ].map(origin => (
                            <button
                                key={origin.id}
                                onClick={() => setOriginFilter(origin.id)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${originFilter === origin.id
                                    ? 'bg-purple-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {origin.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm font-semibold text-slate-700 mr-2">{t('workshop4.ctiDisplay.filterBySeverity')}</span>
                        {[
                            { id: 'ALL', label: t('workshop4.ctiDisplay.all'), color: 'bg-slate-500' },
                            { id: 'CRITICAL', label: t('workshop4.ctiDisplay.critical'), color: 'bg-red-500' },
                            { id: 'HIGH', label: t('workshop4.ctiDisplay.high'), color: 'bg-orange-500' },
                            { id: 'MEDIUM', label: t('workshop4.ctiDisplay.medium'), color: 'bg-yellow-500' },
                            { id: 'LOW', label: t('workshop4.ctiDisplay.low'), color: 'bg-green-500' }
                        ].map(severity => (
                            <button
                                key={severity.id}
                                onClick={() => setSeverityFilter(severity.id)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${severityFilter === severity.id
                                    ? `${severity.color} text-white shadow-md`
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {severity.label} ({severityCounts[severity.id]})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {activeTab === 'vulnerabilities' ? renderVulnerabilitiesContent() : renderTechniquesContent()}

            <div className="flex justify-between items-center pt-4">
                <button
                    onClick={onSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {t('common.saving')}
                        </>
                    ) : (
                        <>
                            <Save className="h-5 w-5" />
                            {t('common.save')}
                        </>
                    )}
                </button>

                {onDelete && (
                    <button
                        onClick={onDelete}
                        className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        <Trash2 className="h-5 w-5" />
                        {t('common.delete')}
                    </button>
                )}
            </div>
        </div>
    );
};

export default CTIDisplaySimple;